// Resolución manual de geolocalización: herramienta propia de Lynx y de la
// entidad Delivery, agnóstica del provider de origen (ML, Tiendanube,
// WooCommerce, carga manual, lo que venga). No hay ninguna acción específica
// de Mercado Libre acá — esa reconsulta sigue existiendo en el backend para
// quien la necesite (el escáner de Planta la usa sola), pero esta pantalla
// no la expone.
//
// Flujo estilo Google Maps (Fase 2, segunda vuelta): seleccionar un envío
// dispara la búsqueda automáticamente, Lynx propone el mejor candidato
// (nunca lo confirma solo) y el operador revisa/corrige/confirma. El pin
// propuesto se colorea según confidence (verde/amarillo); si no hay una
// propuesta defendible, no se coloca ningún pin automático — el operador
// arranca de cero, a mano.
//
// CP recibido vs. confirmado (Fase 2): deliveries.codigo_postal es el
// original tal cual llegó y nunca se pisa. confirmed_postal_code es lo que
// el operador corrige/confirma acá adentro, opcional y con acción propia.
//
// La búsqueda de direcciones llama a la API propia de Lynx
// (GET /v2/deliveries/geo/search), nunca a LocationIQ directamente; y la
// respuesta ya viene con confidence calculada server-side
// (geocoding/confidence.js) — esta pantalla solo la pinta, no la recalcula.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Card from "../ui/Card";
import Button from "../ui/Button";
import Input from "../ui/Input";
import { useLeaflet } from "../../hooks/useLeaflet";
import { buildGeocodeQuery, effectivePostalCode, GeocodeCandidate } from "./geoQuery";

type GeoRow = {
  id: number;
  tracking: string | number | null;
  store: string | null;
  receiver: string | null;
  direccion: string | null;
  codigo_postal: string | null;
  confirmed_postal_code: string | null;
  postal_code_norm: string | null;
  localidad: string | null;
  provincia: string | null;
  delivery_latitude: number | null;
  delivery_longitude: number | null;
  ops_status: string;
  geo_status: string;
  created_at: string | null;
  reasons: string[];
};

type ShelfInfo = {
  shelved: boolean;
  misplaced: boolean;
  current_shelf?: { id: number; code: string };
  expected_shelf?: { id: number; code: string; unclassified: boolean };
};

type ConfidenceLevel = "high" | "medium" | "low";
type Pin = { lat: number; lng: number; source: "auto" | "manual"; level?: ConfidenceLevel };

// Procesamiento masivo (Fase 2, cierre): preview/confirm de dos pasos. La
// UI hace UNA sola request por paso — todas las llamadas al geocoder las
// hace el backend (ver batchGeocodeService). Acá solo se pinta el resultado
// y se dispara la confirmación explícita.
type BatchPreviewItem = {
  deliveryId: number;
  tracking: string | number | null;
  store: string | null;
  direccion: string | null;
  codigoPostal: string | null;
  localidad: string | null;
  provincia: string | null;
  level: ConfidenceLevel | "error";
  candidate: GeocodeCandidate | null;
  error?: string;
};
type BatchPreview = {
  previewId: string;
  evaluated: number;
  high: number;
  medium: number;
  low: number;
  errors: number;
  items: BatchPreviewItem[];
};
type BatchResult = {
  confirmed: { deliveryId: number; tracking: string | number | null; misplaced: boolean }[];
  already_resolved: { deliveryId: number; tracking: string | number | null }[];
  errors: { deliveryId: number; tracking: string | number | null; error: string }[];
  medium_pending: number;
  low_pending: number;
};

const REASON_LABEL: Record<string, string> = {
  coords: "sin coordenadas",
  postal: "CP ilegible",
  address: "sin dirección",
};

// Mismo centro que Choferes en vivo (AMBA): es donde arranca la vista hasta
// que el operador navega o hay una coordenada candidata para centrar.
const CENTRO: [number, number] = [-34.62, -58.45];
const ZOOM_DEFAULT = 9;
const ZOOM_APROX = 12;
const ZOOM_SELECCION = 16;

const LEVEL_COLOR: Record<ConfidenceLevel, string> = {
  high: "var(--success)",
  medium: "var(--warning, #d29922)",
  low: "var(--danger)",
};
const LEVEL_LABEL: Record<ConfidenceLevel, string> = { high: "Alta", medium: "Media", low: "Baja" };
const MANUAL_COLOR = "var(--primary, #4f8cff)";
const LEVEL_RANK: Record<ConfidenceLevel, number> = { high: 2, medium: 1, low: 0 };

const smallMutedStyle: React.CSSProperties = { fontSize: "12px", color: "var(--text-secondary)" };
const panelStyle: React.CSSProperties = { border: "1px solid var(--border)", borderRadius: "18px", background: "rgba(255,255,255,0.03)", padding: "14px" };
const badge = (color: string): React.CSSProperties => ({ display: "inline-block", padding: "3px 10px", borderRadius: "999px", fontSize: "12px", fontWeight: 600, border: `1px solid ${color}`, color });
const WARN = "var(--warning, #d29922)";

const rowStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "2px",
  padding: "8px 10px",
  borderRadius: "10px",
  cursor: "pointer",
  borderLeft: "3px solid transparent",
};

const candidateRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: "8px",
  padding: "8px 10px",
  borderRadius: "10px",
  cursor: "pointer",
  border: "1px solid var(--border)",
};

const dotStyle = (color: string): React.CSSProperties => ({
  display: "inline-block",
  width: "9px",
  height: "9px",
  borderRadius: "50%",
  background: color,
  flexShrink: 0,
});

const linkButtonStyle: React.CSSProperties = {
  background: "transparent",
  border: "none",
  color: "var(--primary, #4f8cff)",
  cursor: "pointer",
  fontSize: "12px",
  fontWeight: 600,
  padding: 0,
};

function pickProposal(candidates: GeocodeCandidate[]): GeocodeCandidate | null {
  if (!candidates.length) return null;
  return [...candidates].sort((a, b) => LEVEL_RANK[b.confidence.level] - LEVEL_RANK[a.confidence.level])[0];
}

type ReqFn = (path: string, init?: { method?: string; body?: unknown }) => Promise<any>;

export default function GeoResolutionPanel({
  req,
  onError,
  onNotice,
}: {
  req: ReqFn;
  onError: (m: string | null) => void;
  onNotice: (m: string) => void;
}) {
  const leaflet = useLeaflet();
  const [rows, setRows] = useState<GeoRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [pin, setPin] = useState<Pin | null>(null);
  const [saving, setSaving] = useState(false);

  const [addrInput, setAddrInput] = useState("");
  const [searchBusy, setSearchBusy] = useState(false);
  const [searchMsg, setSearchMsg] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<GeocodeCandidate[]>([]);

  const [cpConfirmado, setCpConfirmado] = useState("");
  const [showCpEdit, setShowCpEdit] = useState(false);

  // Confidence calculada durante esta sesión, por Delivery. No hay ninguna
  // clasificación persistida hoy (confirmado: geo_status/geo_source no
  // guardan nada de esto) — nace vacío en cada carga de la pantalla y solo
  // se completa a medida que el operador va seleccionando envíos, nunca en
  // bloque. Ver informe: procesar los ~cientos de pendientes de una sola
  // vez implicaría esa misma cantidad de requests a LocationIQ, que es
  // exactamente lo que se pidió no hacer en esta iteración.
  const [sessionConfidence, setSessionConfidence] = useState<Record<number, ConfidenceLevel>>({});

  const mapRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [batchPreview, setBatchPreview] = useState<BatchPreview | null>(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchConfirming, setBatchConfirming] = useState(false);
  const [batchResult, setBatchResult] = useState<BatchResult | null>(null);
  const [showGreenDetail, setShowGreenDetail] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows((await req("/v2/deliveries/geo/pending?limit=500")).deliveries ?? []);
    } catch (e: any) {
      onError(e.message);
    } finally {
      setLoading(false);
    }
  }, [req, onError]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.tracking, r.store, r.direccion, r.localidad, r.codigo_postal, r.provincia]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [rows, search]);

  const selected = useMemo(() => rows.find((r) => r.id === selectedId) ?? null, [rows, selectedId]);

  // CP operativo efectivo PARA BUSCAR: si el operador ya está corrigiendo el
  // CP (borrador, todavía sin confirmar), usa eso; si no, el ya confirmado
  // o, en su defecto, el recibido. Nunca dispara una búsqueda por sí solo.
  const cpEfectivo = selected
    ? (cpConfirmado.trim() || effectivePostalCode(selected.codigo_postal, selected.confirmed_postal_code))
    : "";

  // El mapa se crea una sola vez. El contenedor está SIEMPRE en el DOM
  // (nunca depende de `selected`): si se desmonta y se vuelve a montar,
  // este efecto no vuelve a correr (solo depende de `leaflet`) y el mapa ya
  // creado queda apuntando a un nodo removido — pantalla negra.
  useEffect(() => {
    if (!leaflet || !containerRef.current || mapRef.current) return;
    const L = (window as any).L;
    const map = L.map(containerRef.current, { zoomControl: true }).setView(CENTRO, ZOOM_DEFAULT);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 18, attribution: "© OpenStreetMap" }).addTo(map);
    map.on("click", (e: any) => setPin({ lat: e.latlng.lat, lng: e.latlng.lng, source: "manual" }));
    mapRef.current = map;
  }, [leaflet]);

  // Arma la búsqueda con SOLO estos 4 campos (nunca observaciones, nunca el
  // provider de origen) y llama a la API propia de Lynx. `direccionTexto`
  // es lo que se ve/edita en el buscador único; el resto son los campos
  // fijos del Delivery (o el CP corregido si el operador lo está editando).
  const buscar = useCallback(
    async (direccionTexto: string, row: GeoRow, cpParaBuscar: string) => {
      setSearchBusy(true);
      setSearchMsg(null);
      setCandidates([]);
      try {
        const params = new URLSearchParams();
        if (direccionTexto.trim()) params.set("direccion", direccionTexto.trim());
        if (cpParaBuscar) params.set("codigo_postal", cpParaBuscar);
        if (row.localidad) params.set("localidad", row.localidad);
        if (row.provincia) params.set("provincia", row.provincia);

        const json = await req(`/v2/deliveries/geo/search?${params.toString()}`);
        const found: GeocodeCandidate[] = json.candidates ?? [];
        setCandidates(found);

        const best = pickProposal(found);
        setSessionConfidence((prev) => ({ ...prev, [row.id]: best ? best.confidence.level : "low" }));

        const map = mapRef.current;
        if (best && best.confidence.level !== "low") {
          setPin({ lat: best.latitude, lng: best.longitude, source: "auto", level: best.confidence.level });
          if (map) map.setView([best.latitude, best.longitude], ZOOM_SELECCION);
        } else {
          // Rojo (o sin candidatos): nunca un pin automático engañoso. Si
          // hay al menos un candidato, centramos ahí no más como contexto,
          // con un zoom bien abierto para que quede claro que es aproximado.
          setPin(null);
          if (best && map) map.setView([best.latitude, best.longitude], ZOOM_APROX);
          if (!found.length) setSearchMsg("Sin resultados para esta búsqueda.");
        }
      } catch (e: any) {
        setSearchMsg(e.message);
        setCandidates([]);
      } finally {
        setSearchBusy(false);
      }
    },
    [req]
  );

  // Al elegir un envío: precarga el buscador único con dirección + CP
  // operativo + localidad + provincia, y dispara la búsqueda automática.
  // Si ya traía coordenadas candidatas propias (no 0/0), esas priman como
  // punto de partida visual mientras llega la respuesta del geocoder.
  useEffect(() => {
    setSearchMsg(null);
    setCandidates([]);
    setShowCpEdit(false);
    if (!selected) {
      setPin(null);
      setAddrInput("");
      setCpConfirmado("");
      return;
    }
    const cpEf = effectivePostalCode(selected.codigo_postal, selected.confirmed_postal_code);
    const q = buildGeocodeQuery({
      direccion: selected.direccion, codigoPostal: cpEf, localidad: selected.localidad, provincia: selected.provincia,
    });
    setAddrInput(q);
    setCpConfirmado(selected.confirmed_postal_code || "");
    setShowCpEdit(Boolean(selected.confirmed_postal_code));

    const { delivery_latitude: lat, delivery_longitude: lng } = selected;
    const candidato = lat != null && lng != null && lat !== 0 && lng !== 0 ? { lat, lng } : null;
    const map = mapRef.current;
    if (candidato) {
      setPin({ lat: candidato.lat, lng: candidato.lng, source: "auto" });
      if (map) map.setView([candidato.lat, candidato.lng], ZOOM_SELECCION);
    } else {
      setPin(null);
      if (map) map.setView(CENTRO, ZOOM_DEFAULT);
    }

    buscar(selected.direccion || "", selected, cpEf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  // El marcador sigue al estado `pin`: automático (verde/amarillo), elegido
  // de la lista de candidatos, o colocado/arrastrado a mano (siempre azul,
  // para que se note que es una decisión del operador y no una propuesta).
  useEffect(() => {
    const L = (window as any).L;
    const map = mapRef.current;
    if (!L || !map) return;

    if (!pin) {
      markerRef.current?.remove();
      markerRef.current = null;
      return;
    }

    const color = pin.source === "manual" ? MANUAL_COLOR : LEVEL_COLOR[pin.level ?? "low"];
    const icon = L.divIcon({
      className: "",
      html: `<div style="width:22px;height:22px;border-radius:50% 50% 50% 0;background:${color};border:2px solid #1a1a1a;transform:rotate(-45deg);box-shadow:0 1px 3px rgba(0,0,0,0.4);"></div>`,
      iconSize: [22, 22],
      iconAnchor: [11, 22],
    });

    if (!markerRef.current) {
      const marker = L.marker([pin.lat, pin.lng], { icon, draggable: true }).addTo(map);
      marker.on("dragend", () => {
        const ll = marker.getLatLng();
        setPin({ lat: ll.lat, lng: ll.lng, source: "manual" });
      });
      markerRef.current = marker;
    } else {
      markerRef.current.setLatLng([pin.lat, pin.lng]);
      markerRef.current.setIcon(icon);
    }
  }, [pin, leaflet]);

  const buscarDeNuevo = () => {
    if (!selected) return;
    buscar(addrInput, selected, cpEfectivo);
  };

  // Elegir otro candidato de la lista: mueve mapa y pin, recalcula qué
  // color mostrar (la confidence de ESE candidato ya vino calculada del
  // backend, no se recalcula acá). Nunca confirma solo.
  const elegirCandidato = (c: GeocodeCandidate) => {
    setPin({ lat: c.latitude, lng: c.longitude, source: "auto", level: c.confidence.level });
    const map = mapRef.current;
    if (map) map.setView([c.latitude, c.longitude], ZOOM_SELECCION);
    if (c.postalCode && c.postalCode !== cpEfectivo) {
      setCpConfirmado(c.postalCode);
      setShowCpEdit(true);
    }
  };

  const confirmar = async () => {
    if (!selected || !pin) return;
    setSaving(true);
    onError(null);
    try {
      const body: Record<string, unknown> = { latitude: pin.lat, longitude: pin.lng };
      const cp = cpConfirmado.trim();
      if (cp) body.confirmed_postal_code = cp;

      const json = await req(`/v2/deliveries/${selected.id}/geo`, { method: "PUT", body });
      const shelf: ShelfInfo | undefined = json?.shelf;
      const nombre = selected.tracking ?? selected.id;
      if (shelf?.misplaced) {
        onNotice(
          `${nombre}: ubicación confirmada. Sigue en ${shelf.current_shelf?.code}, pero por esta ubicación le correspondería ${shelf.expected_shelf?.code} — quedó marcado como fuera de lugar para moverlo desde esa bandeja.`
        );
      } else {
        onNotice(`${nombre}: ubicación confirmada.`);
      }
      setSelectedId(null);
      load();
    } catch (e: any) {
      onError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // Paso 1: le pide al backend que geocodifique los pendientes y evalúe
  // confianza. Ninguna escritura en la base todavía — solo guarda un
  // preview del lado del servidor, identificado por previewId.
  const evaluarPendientes = async () => {
    setBatchLoading(true);
    onError(null);
    setBatchResult(null);
    setShowGreenDetail(false);
    try {
      const json = await req("/v2/deliveries/geo/batch/preview", { method: "POST", body: {} });
      setBatchPreview(json);
    } catch (e: any) {
      onError(e.message);
    } finally {
      setBatchLoading(false);
    }
  };

  // Paso 2: acción de ADMIN explícita, con diálogo de confirmación mostrando
  // la cantidad exacta. Aplica EXACTAMENTE los candidatos verdes ya
  // evaluados en el preview — nunca vuelve a geocodificar.
  const confirmarLote = async () => {
    if (!batchPreview || !batchPreview.high) return;
    const ok = window.confirm(
      `Confirmar ${batchPreview.high} ubicación${batchPreview.high === 1 ? "" : "es"} de alta confianza. Esta acción va a marcar esos envíos como resueltos. ¿Continuar?`
    );
    if (!ok) return;
    setBatchConfirming(true);
    onError(null);
    try {
      const json = await req("/v2/deliveries/geo/batch/confirm", {
        method: "POST",
        body: { preview_id: batchPreview.previewId },
      });
      setBatchResult(json);
      setBatchPreview(null);
      setShowGreenDetail(false);
      load();
    } catch (e: any) {
      onError(e.message);
    } finally {
      setBatchConfirming(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <Card padding="14px">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
          <div>
            <div style={{ color: "var(--text-primary)", fontWeight: 700, fontSize: "14px" }}>Procesamiento masivo</div>
            <div style={smallMutedStyle}>
              Evalúa los pendientes contra el geocoder y permite confirmar en bloque solo los de confianza alta (🟢). Amarillos y rojos siempre se revisan uno por uno.
            </div>
          </div>
          <Button variant="secondary" onClick={evaluarPendientes} disabled={batchLoading || batchConfirming}>
            {batchLoading ? "Evaluando…" : "Evaluar pendientes"}
          </Button>
        </div>

        {batchPreview ? (
          <div style={{ marginTop: "12px" }}>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
              <span style={badge(LEVEL_COLOR.high)}>🟢 {batchPreview.high} alta</span>
              <span style={badge(LEVEL_COLOR.medium)}>🟡 {batchPreview.medium} media</span>
              <span style={badge(LEVEL_COLOR.low)}>🔴 {batchPreview.low} baja</span>
              {batchPreview.errors ? <span style={badge(WARN)}>{batchPreview.errors} con error</span> : null}
              <span style={smallMutedStyle}>de {batchPreview.evaluated} evaluados</span>
            </div>

            {batchPreview.high > 0 ? (
              <>
                <button type="button" style={{ ...linkButtonStyle, marginTop: "10px" }} onClick={() => setShowGreenDetail((v) => !v)}>
                  {showGreenDetail ? "Ocultar detalle de los verdes" : `Revisar los ${batchPreview.high} verdes`}
                </button>
                {showGreenDetail ? (
                  <div style={{ marginTop: "8px", maxHeight: "260px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "4px" }}>
                    {batchPreview.items.filter((it) => it.level === "high").map((it) => (
                      <div key={it.deliveryId} style={{ ...smallMutedStyle, fontSize: "12px", padding: "4px 8px", borderRadius: "8px", background: "rgba(255,255,255,0.03)" }}>
                        <span style={{ fontFamily: "monospace", color: "var(--text-primary)" }}>{it.tracking ?? it.deliveryId}</span>
                        {" · "}{it.store ?? "—"} · {it.direccion || "sin dirección"} → {it.candidate?.address || it.candidate?.displayName || "—"}
                        {it.candidate?.confidence.reasons?.length ? ` (${it.candidate.confidence.reasons.join(", ")})` : ""}
                      </div>
                    ))}
                  </div>
                ) : null}

                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "10px" }}>
                  <Button onClick={confirmarLote} disabled={batchConfirming}>
                    {batchConfirming ? "Confirmando…" : `Confirmar ${batchPreview.high} ubicaciones`}
                  </Button>
                </div>
              </>
            ) : (
              <div style={{ ...smallMutedStyle, marginTop: "10px" }}>No hay candidatos de confianza alta para confirmar en bloque.</div>
            )}
          </div>
        ) : null}

        {batchResult ? (
          <div style={{ ...smallMutedStyle, marginTop: "12px", fontSize: "13px", color: "var(--text-primary)" }}>
            Confirmadas: {batchResult.confirmed.length} / Ya resueltas por otro proceso: {batchResult.already_resolved.length} / Errores: {batchResult.errors.length} / Pendientes amarillos: {batchResult.medium_pending} / Pendientes rojos: {batchResult.low_pending}
            {batchResult.confirmed.some((c) => c.misplaced) ? (
              <div style={{ marginTop: "4px" }}>
                Algunos quedaron marcados como fuera de lugar (ver bandeja de estanteado) — el estante nunca se mueve solo.
              </div>
            ) : null}
          </div>
        ) : null}
      </Card>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(300px, 38fr) minmax(440px, 62fr)", gap: "16px" }}>
      <Card padding="12px">
        <div style={{ padding: "2px 4px 10px 4px" }}>
          <Input
            value={search}
            onChange={(e: any) => setSearch(e.target.value)}
            placeholder="Buscar por tracking, tienda, dirección, localidad, CP o provincia…"
          />
        </div>
        <div style={{ ...smallMutedStyle, padding: "0 4px 8px 4px" }}>
          {loading ? "Cargando…" : `${filtered.length} de ${rows.length} pendiente${rows.length === 1 ? "" : "s"}`}
        </div>
        <div style={{ maxHeight: "620px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "2px" }}>
          {!loading && filtered.length === 0 ? (
            <div style={{ ...smallMutedStyle, padding: "10px" }}>
              {rows.length === 0 ? "No hay pendientes de geolocalización." : "Nada para ese filtro."}
            </div>
          ) : (
            filtered.map((r) => {
              const level = sessionConfidence[r.id];
              return (
                <div
                  key={r.id}
                  onClick={() => setSelectedId(r.id)}
                  style={{
                    ...rowStyle,
                    background: selectedId === r.id ? "rgba(255,255,255,0.06)" : "transparent",
                    borderLeftColor: selectedId === r.id ? "var(--primary, #4f8cff)" : "transparent",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: "8px", alignItems: "center" }}>
                    <span style={{ display: "flex", alignItems: "center", gap: "6px", fontFamily: "monospace", fontWeight: 700, color: "var(--text-primary)", fontSize: "13px" }}>
                      {level ? <span style={dotStyle(LEVEL_COLOR[level])} title={`Confianza ${LEVEL_LABEL[level]}`} /> : null}
                      {r.tracking ?? r.id}
                    </span>
                    <span style={{ ...smallMutedStyle, whiteSpace: "nowrap" }}>{r.store ?? "—"}</span>
                  </div>
                  <div style={{ ...smallMutedStyle, fontSize: "12px" }}>{r.direccion || "sin dirección"}</div>
                  <div style={{ ...smallMutedStyle, fontSize: "12px" }}>
                    {[r.localidad, r.codigo_postal].filter(Boolean).join(" · ") || "—"}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </Card>

      <Card padding="12px">
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {!selected ? (
            <div style={{ ...panelStyle, color: "var(--text-secondary)" }}>
              Elegí un envío de la lista: Lynx va a buscar automáticamente dónde cree que está. Mientras tanto podés navegar el mapa libremente.
            </div>
          ) : (
            <div style={panelStyle}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", flexWrap: "wrap", alignItems: "baseline" }}>
                <div>
                  <div style={{ fontFamily: "monospace", fontWeight: 700, color: "var(--text-primary)", fontSize: "15px" }}>
                    {selected.tracking ?? selected.id}
                  </div>
                  <div style={{ color: "var(--text-primary)", fontSize: "14px" }}>{selected.store ?? "—"}</div>
                </div>
                {selected.reasons.length ? (
                  <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                    {selected.reasons.map((r) => (
                      <span key={r} style={badge(WARN)}>{REASON_LABEL[r] ?? r}</span>
                    ))}
                  </div>
                ) : null}
              </div>

              {/* Info secundaria y discreta: lo que llegó, tal cual. */}
              <div style={{ ...smallMutedStyle, marginTop: "8px", fontSize: "12px" }}>
                Dirección recibida: {selected.direccion || "—"} · CP {selected.codigo_postal || "—"} · {selected.localidad || "—"} · {selected.provincia || "—"}
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", marginTop: "8px" }}>
                {pin ? (
                  <span style={badge(pin.source === "manual" ? MANUAL_COLOR : LEVEL_COLOR[pin.level ?? "low"])}>
                    {pin.source === "manual" ? "Pin manual" : `Propuesta Lynx · confianza ${LEVEL_LABEL[pin.level ?? "low"]}`}
                  </span>
                ) : (
                  <span style={{ ...smallMutedStyle }}>Sin propuesta confiable todavía — ubicá el punto a mano.</span>
                )}
                {searchBusy ? <span style={smallMutedStyle}>Buscando…</span> : null}
              </div>
            </div>
          )}

          {/* El contenedor del mapa NUNCA se desmonta según `selected`: Leaflet
              se crea una sola vez sobre este nodo (ver el efecto de arriba) y
              si el nodo desaparece y vuelve a aparecer, el mapa ya creado
              queda apuntando a un DOM removido — por eso siempre está acá,
              aunque no haya nada elegido todavía. */}
          <div
            ref={containerRef}
            style={{ height: "420px", borderRadius: "12px", overflow: "hidden", background: "var(--surface-2, #0c0e12)" }}
          >
            {!leaflet ? <div style={{ ...smallMutedStyle, padding: "16px" }}>Cargando el mapa…</div> : null}
          </div>

          {selected ? (
            <>
              <div style={smallMutedStyle}>
                Clic en el mapa para colocar el pin. Arrastralo para ajustarlo.
              </div>

              <div style={panelStyle}>
                <div style={{ display: "flex", gap: "8px", alignItems: "end", flexWrap: "wrap" }}>
                  <div style={{ flex: 1, minWidth: "260px" }}>
                    <Input
                      label="Buscar ubicación"
                      value={addrInput}
                      onChange={(e: any) => setAddrInput(e.target.value)}
                      onKeyDown={(e: any) => { if (e.key === "Enter") buscarDeNuevo(); }}
                      placeholder="San Martín 1250, 1706, Haedo, Buenos Aires"
                    />
                  </div>
                  <Button variant="secondary" onClick={buscarDeNuevo} disabled={searchBusy || !addrInput.trim()}>
                    {searchBusy ? "Buscando…" : "Buscar de nuevo"}
                  </Button>
                </div>
                <div style={{ ...smallMutedStyle, marginTop: "6px" }}>
                  Editar este texto no modifica la dirección del envío — es solo para buscar.
                </div>
                {searchMsg ? <div style={{ ...smallMutedStyle, marginTop: "8px" }}>{searchMsg}</div> : null}

                {candidates.length > 0 ? (
                  <div style={{ marginTop: "12px", display: "flex", flexDirection: "column", gap: "6px" }}>
                    {candidates.map((c, i) => (
                      <div key={i} onClick={() => elegirCandidato(c)} style={candidateRowStyle}>
                        <span style={{ ...dotStyle(LEVEL_COLOR[c.confidence.level]), marginTop: "4px" }} />
                        <div>
                          <div style={{ color: "var(--text-primary)", fontSize: "13px", fontWeight: 600 }}>
                            {c.address || c.displayName || "Sin nombre"}
                          </div>
                          <div style={{ ...smallMutedStyle, fontSize: "12px" }}>
                            {[c.locality, c.province, c.postalCode ? `CP ${c.postalCode}` : null].filter(Boolean).join(" · ")}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}

                {/* Corrección de CP: acción secundaria, no una segunda fila fija del formulario. */}
                <div style={{ marginTop: "12px" }}>
                  {!showCpEdit ? (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span style={smallMutedStyle}>
                        CP operativo: <span style={{ color: "var(--text-primary)", fontFamily: "monospace" }}>{cpEfectivo || "—"}</span>
                      </span>
                      <button type="button" style={linkButtonStyle} onClick={() => setShowCpEdit(true)}>Corregir CP</button>
                    </div>
                  ) : (
                    <>
                      <Input
                        label="Confirmar / corregir código postal (opcional)"
                        value={cpConfirmado}
                        onChange={(e: any) => setCpConfirmado(e.target.value)}
                        placeholder={selected.codigo_postal || "1704"}
                      />
                      <div style={{ ...smallMutedStyle, marginTop: "4px", display: "flex", justifyContent: "space-between", gap: "8px" }}>
                        <span>El CP recibido queda intacto. Esto solo se guarda como corrección de Lynx.</span>
                        <button type="button" style={linkButtonStyle} onClick={() => { setShowCpEdit(false); setCpConfirmado(selected.confirmed_postal_code || ""); }}>
                          Cerrar
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                <div style={{ ...smallMutedStyle, fontFamily: pin ? "monospace" : undefined }}>
                  {pin
                    ? `Se va a guardar: ${pin.lat.toFixed(5)}, ${pin.lng.toFixed(5)}${cpConfirmado.trim() ? ` · CP confirmado: ${cpConfirmado.trim()}` : ""}`
                    : "Todavía no hay un pin para confirmar."}
                </div>
                <div style={{ display: "flex", gap: "10px" }}>
                  <Button variant="ghost" onClick={() => setSelectedId(null)}>Cancelar</Button>
                  <Button onClick={confirmar} disabled={saving || !pin}>
                    {saving ? "Confirmando…" : "Confirmar ubicación"}
                  </Button>
                </div>
              </div>
            </>
          ) : null}
        </div>
      </Card>
      </div>
    </div>
  );
}
